import formal_document_entity_type_selector_component from "@/components/probaworkflows_base_components/FormalDocumentTemplateLibrary/FormalDocumentEntityTypeSelector.vue";
import formal_document_question_selector_component from "@/components/probaworkflows_base_components/FormalDocumentTemplateLibrary/FormalDocumentQuestionSelector.vue";
import formal_document_template_preview_component from "@/components/probaworkflows_base_components/FormalDocumentTemplateLibrary/FormalDocumentTemplatePreview.vue";
import formal_document_template_section_editor_component from "@/components/probaworkflows_base_components/FormalDocumentTemplateLibrary/FormalDocumentTemplateSectionEditor.vue";
import pdf_report_signature_modal_component from "@/components/probaworkflows_base_components/PDFReportSignatureModal/PDFReportSignatureModal.vue";
import {
  GET_ENTITY_TYPE_ASSIGNED_QUESTIONS,
} from "@/utils/constants/constants";
import {
  normalize_admin_signature_payload,
  sections_require_admin_signature,
} from "@/utils/pdf_report_signature_helpers";
import alert_mixin from "@/utils/mixins/alert_mixin";
import axios from "axios";
import drag_and_drop_mixin from "@/utils/mixins/drag_and_drop_mixin";
import formal_document_template_wizard_action_methods from "./formal_document_template_wizard_action_methods";
import formal_document_template_wizard_preview_methods from "./formal_document_template_wizard_preview_methods";

import {
  build_checklist_row,
  build_field_item,
  build_section_form,
  build_table_column,
  build_template_form,
} from "./formal_document_template_helpers";
import {
  build_formal_question_options,
  build_step_validation_message,
} from "./formal_document_template_wizard_helpers";
import {
  formal_document_template_wizard_computed,
  formal_document_template_wizard_data,
  formal_document_template_wizard_watch,
  get_default_section_type,
} from "./formal_document_template_wizard_config";
import {
  rebuild_static_rows_for_columns,
  sync_question_bound_columns,
  sync_question_bound_rows,
} from "./formal_document_template_selection_helpers";
import formal_document_template_wizard_scope_methods from "./formal_document_template_wizard_scope_methods";

export default {
  name: "FormalDocumentTemplateWizard",
  components: {
    FormalDocumentEntityTypeSelector:
      formal_document_entity_type_selector_component,
    FormalDocumentQuestionSelector: formal_document_question_selector_component,
    FormalDocumentTemplatePreview: formal_document_template_preview_component,
    FormalDocumentTemplateSectionEditor:
      formal_document_template_section_editor_component,
    PDFReportSignatureModal: pdf_report_signature_modal_component,
  },
  mixins: [alert_mixin, drag_and_drop_mixin],
  props: {
    initial_template: {
      type: Object,
      default: null,
    },
    cancel_label: {
      type: String,
      default: "Cancel",
    },
    report_lookups: {
      type: Object,
      default: () => ({}),
    },
    formal_lookups: {
      type: Object,
      default: () => ({}),
    },
    allow_one_off: {
      type: Boolean,
      default: false,
    },
    selected_output_format_id: {
      type: [Number, String],
      default: "",
    },
    initial_target_entity_profile_id: {
      type: [Number, String],
      default: null,
    },
    initial_application_id: {
      type: [Number, String],
      default: null,
    },
    initial_entity_type_id: {
      type: [Number, String],
      default: null,
    },
    initial_entity_name: {
      type: String,
      default: "",
    },
  },
  emits: ["cancel", "saved", "generated", "change-output-format"],
  data: formal_document_template_wizard_data,
  computed: formal_document_template_wizard_computed,
  watch: formal_document_template_wizard_watch,
  beforeDestroy() {
    this.clear_preview_refresh_timer();
    this.clear_logo_preview_url();
  },
  methods: {
    reset_wizard() {
      this.clear_logo_preview_url();
      this.template_form = build_template_form(this.initial_template, this.project_id);
      this.generated_document = null;
      this.signature_modal_visible = false;
      this.pending_one_off_generation = null;
      this.preview_html = "";
      this.preview_error = "";
      this.ensure_locked_profile_entity_type();
      this.handle_template_save_mode_change();
      this.logo_preview_url = String(this.initial_template?.logo_preview_url || "");
      this.ensure_default_section();
      this.current_step_key = "basics";
      this.active_section_local_id = this.template_form.sections[0]?.local_id || "";
      this.new_section_type = get_default_section_type(this.formal_lookups);
      this.refresh_question_options();
      this.schedule_preview_refresh();
    },
    ensure_default_section() {
      if ((this.template_form.sections || []).length) {
        return;
      }
      this.template_form.sections.push(build_section_form("document_title", 1));
    },
    refresh_question_options() {
      const entity_type_ids = this.template_form?.entity_type_ids || [];
      if (!entity_type_ids.length) {
        this.question_options = [];
        return;
      }
      this.loading_questions = true;
      Promise.all(entity_type_ids.map((entity_type_id) => this.get_question_request(entity_type_id)))
        .then((responses) => {
          this.question_options = build_formal_question_options(responses);
        })
        .catch((error) => {
          this.show_error(
            error.response?.data?.message || "Failed to load questions for the selected entity types."
          );
        })
        .finally(() => {
          this.loading_questions = false;
        });
    },
    get_question_request(entity_type_id) {
      return axios.get(GET_ENTITY_TYPE_ASSIGNED_QUESTIONS, {
        headers: this.headers,
        params: { entity_type_id },
      });
    },
    get_step_status(step_key) {
      const target_index = this.wizard_steps.findIndex((step) => step.key === step_key);
      if (target_index === this.current_step_index) {
        return "active";
      }
      if (target_index < this.current_step_index) {
        return "complete";
      }
      return "upcoming";
    },
    open_step(step_key) {
      const target_index = this.wizard_steps.findIndex((step) => step.key === step_key);
      if (target_index <= this.current_step_index + 1) {
        this.current_step_key = step_key;
      }
    },
    go_to_previous_step() {
      if (this.current_step_index <= 0) {
        return;
      }
      this.current_step_key = this.wizard_steps[this.current_step_index - 1].key;
    },
    go_to_next_step() {
      const validation_message = build_step_validation_message(
        this.current_step_key,
        this.template_form
      );
      if (validation_message) {
        this.show_info(validation_message);
        return;
      }
      if (this.current_step_index >= this.wizard_steps.length - 1) {
        return;
      }
      this.current_step_key = this.wizard_steps[this.current_step_index + 1].key;
    },
    handle_generation_mode_change() {
      if (this.template_form.generation_mode === "manual") {
        this.template_form.trigger_status_id = "";
      }
    },
    handle_output_format_change(next_output_format_id) {
      this.$emit("change-output-format", next_output_format_id);
    },
    pdf_requires_admin_signature() {
      return sections_require_admin_signature(this.template_form?.sections || []);
    },
    open_signature_modal(pending_generation) {
      this.pending_one_off_generation = pending_generation;
      this.signature_modal_visible = true;
    },
    close_signature_modal() {
      this.signature_modal_visible = false;
      this.pending_one_off_generation = null;
    },
    confirm_signature(signature_payload) {
      const pending_generation = this.pending_one_off_generation;
      this.signature_modal_visible = false;
      this.pending_one_off_generation = null;
      if (!pending_generation) {
        return;
      }
      this.begin_one_off_generation(
        pending_generation.config_payload,
        normalize_admin_signature_payload(signature_payload)
      );
    },
    begin_one_off_generation(config_payload, admin_signature = null) {
      this.saving_template = true;
      this.show_loader("Generating one-off PDF...").then((loader) => {
        this.submit_one_off_template(config_payload, loader, admin_signature);
      });
    },
    open_question_picker(payload) {
      this.question_picker_state = {
        modal_title: payload.selection_mode === "multiple"
          ? "Choose linked questions"
          : "Choose linked question",
        section_local_id: payload.section_local_id,
        selected_question_ids: payload.selected_question_ids || [],
        selection_mode: payload.selection_mode || "single",
        target_local_id: payload.target_local_id,
        target_type: payload.target_type,
        visible: true,
      };
    },
    close_question_picker() {
      this.question_picker_state = {
        modal_title: "Choose linked question",
        section_local_id: "",
        selected_question_ids: [],
        selection_mode: "single",
        target_local_id: "",
        target_type: "",
        visible: false,
      };
    },
    apply_question_picker_selection(selected_questions) {
      const target_section = this.find_picker_target_section();
      if (!target_section) {
        this.close_question_picker();
        return;
      }
      const selected_ids = selected_questions.map((question) => String(question.id));
      this.apply_picker_selection_to_section(
        target_section,
        selected_ids,
        selected_questions
      );
      this.close_question_picker();
    },
    find_picker_target_section() {
      const target_section = (this.template_form.sections || []).find(
        (section) => section.local_id === this.question_picker_state.section_local_id
      );

      return target_section;
    },
    sync_bulk_field_items(section, selected_ids) {
      section.config.items = sync_question_bound_rows(
        section.config.items || [],
        selected_ids,
        this.build_bulk_field_item
      );
    },
    sync_bulk_checklist_rows(section, selected_ids) {
      section.config.rows = sync_question_bound_rows(
        section.config.rows || [],
        selected_ids,
        this.build_bulk_checklist_row
      );
    },
    sync_bulk_table_columns(section, selected_ids) {
      const previous_columns = [...(section.config.columns || [])];
      const next_columns = sync_question_bound_columns(
        previous_columns,
        selected_ids,
        this.build_bulk_table_column
      );

      section.config.columns = next_columns;
      if (section.config.row_source?.source_type === "static_rows") {
        section.config.rows = rebuild_static_rows_for_columns(
          section.config.rows || [],
          previous_columns,
          next_columns
        );
      }
    },
    apply_bulk_picker_selection(section, selected_ids) {
      const target_type = this.question_picker_state.target_type;
      let handled_bulk_selection = true;

      if (target_type === "field_items_bulk") {
        this.sync_bulk_field_items(section, selected_ids);
      } else if (target_type === "checklist_rows_bulk") {
        this.sync_bulk_checklist_rows(section, selected_ids);
      } else if (target_type === "table_columns_bulk") {
        this.sync_bulk_table_columns(section, selected_ids);
      } else {
        handled_bulk_selection = false;
      }

      return handled_bulk_selection;
    },
    apply_single_picker_selection(section, selected_ids, selected_questions = []) {
      if (this.question_picker_state.target_type === "row_source_questions") {
        section.config.row_source.question_ids = selected_ids;
        return;
      }

      const target_row = this.find_picker_target_row(section);
      if (!target_row) {
        return;
      }

      const selected_question = selected_questions[0] || null;
      target_row.binding.source_type = "section_question";
      target_row.binding.section_question_id = selected_ids[0] || "";
      target_row.binding.question_label = selected_question?.question_text || "";
      target_row.binding.system_field = "";
      target_row.binding.value = "";
    },
    apply_picker_selection_to_section(section, selected_ids, selected_questions = []) {
      const handled_bulk_selection = this.apply_bulk_picker_selection(
        section,
        selected_ids
      );

      if (handled_bulk_selection) {
        return;
      }

      this.apply_single_picker_selection(
        section,
        selected_ids,
        selected_questions
      );
    },
    build_bulk_field_item(question_id, existing_item = null) {
      const item = existing_item || build_field_item();
      const question_option = this.question_options.find(
        (option) => String(option.id) === String(question_id)
      );
      item.binding.source_type = "section_question";
      item.binding.section_question_id = question_id;
      item.binding.question_label = question_option?.question_text || "";
      item.binding.system_field = "";
      return item;
    },
    build_bulk_checklist_row(question_id, existing_row = null) {
      const row = existing_row || build_checklist_row();
      const question_option = this.question_options.find(
        (option) => String(option.id) === String(question_id)
      );
      row.binding.section_question_id = question_id;
      row.binding.question_label = question_option?.question_text || "";
      return row;
    },
    build_bulk_table_column(question_id, existing_column = null) {
      const column = existing_column || build_table_column();
      const question_option = this.question_options.find(
        (option) => String(option.id) === String(question_id)
      );
      column.binding.source_type = "section_question";
      column.binding.section_question_id = question_id;
      column.binding.question_label = question_option?.question_text || "";
      column.binding.system_field = "";
      return column;
    },
    get_picker_collection(section) {
      let collection = section.config.columns || [];

      if (this.question_picker_state.target_type === "field_item") {
        collection = section.config.items || [];
      } else if (this.question_picker_state.target_type === "checklist_row") {
        collection = section.config.rows || [];
      }

      return collection;
    },
    find_picker_target_row(section) {
      const items = this.get_picker_collection(section);
      const target_row = items.find(
        (item) => item.local_id === this.question_picker_state.target_local_id
      );

      return target_row;
    },
    get_picker_collection_name() {
      let collection_name = "columns";

      if (this.question_picker_state.target_type === "field_item") {
        collection_name = "items";
      } else if (this.question_picker_state.target_type === "checklist_row") {
        collection_name = "rows";
      }

      return collection_name;
    },
    add_section(offset = 1) {
      const next_section = build_section_form(
        this.new_section_type,
        this.template_form.sections.length + 1
      );
      if (!this.active_section_local_id) {
        this.template_form.sections.push(next_section);
      } else {
        const active_index = this.template_form.sections.findIndex(
          (section) => section.local_id === this.active_section_local_id
        );
        const target_index = Math.max(active_index + offset, 0);
        this.template_form.sections.splice(target_index, 0, next_section);
      }
      this.active_section_local_id = next_section.local_id;
    },
    open_reorder_sections_modal() {
      this.current_reorder_sections = [...(this.template_form.sections || [])];
      this.reorder_sections_modal_visible = true;
    },
    close_reorder_sections_modal() {
      this.reorder_sections_modal_visible = false;
      this.current_reorder_sections = [];
    },
    update_section_order() {
      if (!Array.isArray(this.current_reorder_sections)) {
        return;
      }
      this.template_form.sections = this.current_reorder_sections.map(
        (section, index) => ({
          ...section,
          order_index: index + 1,
        })
      );
      this.close_reorder_sections_modal();
    },
    remove_section(local_id) {
      this.template_form.sections = this.template_form.sections.filter(
        (section) => section.local_id !== local_id
      );
      this.ensure_default_section();
      this.active_section_local_id = this.template_form.sections[0]?.local_id || "";
    },
    format_section_type(section_type) {
      return String(section_type || "").split("_").join(" ");
    },
    ...formal_document_template_wizard_scope_methods,
    ...formal_document_template_wizard_preview_methods,
    ...formal_document_template_wizard_action_methods,
  },
};
